<script>
/**
 * Mixin for filter and sorting options in problems.vue and archives.vue
 * Most computed return default values for the main `pbOptions` object.
 * Override any computed property to change the value of `pbOptions`.
 * If `allPageOptions` is defined, the values of `pbOptions` will be defined accordingly,
 * unless the values are overriden specifically.
 *
 * Eg:
 * Priority order for option 'sortBy':
 * Default sortBy : `{ value: 3, desc: true }`
 * Overriden by allPageOptions.sortBy
 * Overriden by component-defined pbOptSortBy
 */

// Vuex
import { mapState } from 'vuex';
import { GET_PB_TYPES, GET_ZONES, GET_USERS } from '@/store/mutation-types';
import { FETCH_PB_TYPES, FETCH_ZONES, FETCH_USERS } from '@/store/action-types';
// Utils
import { nestedFindItemWithId } from '@/utils/main';
import { convertImpactFilterToArray } from '@/utils/problem-utils';
import IAHelp from '@/utils/classes/IAHelp';

export default {
  computed: {
    pbOptions() {
      // Filters and sorting
      return [
        {
          key: 'pbOptOnlytext1',
          value: '',
          component: 'h2',
          options: { class: 'mt5em mb5em basic-sub-title' },
          slots: [{
            component: 'Div',
            content: this.$t('message.WIDGET_PB_SORT'),
          }],
        },
        {
          key: 'sortBy',
          value: this.pbOptSortBy,
          component: 'RadioGroup',
          options: {
            sortable: true,
            items: this.pbOptSortItems,
          },
        },
        {
          key: 'pbOptOnlytext2',
          value: '',
          component: 'h2',
          options: { class: 'mt10em mb5em basic-sub-title' },
          slots: [{
            component: 'Div',
            content: this.$t('message.FILTERS'),
          }],
        },
        {
          key: 'pbOptOnlytext3',
          value: '',
          component: 'Div',
          options: { class: 'mt5 legend legend--bottom-margin' },
          slots: [{
            component: 'Div',
            content: this.$t('message.TYPES'),
          }],
        },
        {
          key: 'typesInclusive',
          value: this.pbOptTypesInclusive,
          component: 'IntCheckbox',
          options: {
            class: 'block',
            title: this.$t('message.TYPE_INCLUSIVE'),
          },
        },
        {
          key: 'selectedTypes',
          component: 'Tree',
          value: this.pbOptSelectedTypes,
          options: {
            ref: 'typesTree',
            multiple: true,
            items: this.pbOptAllTypes,
            childrenKey: 'types',
            nameKey: 'name',
          },
        },
        {
          key: 'pbOptSep1',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        },
        {
          key: 'pbOptOnlytext4',
          value: '',
          component: 'Div',
          options: { class: 'mt5 legend legend--bottom-margin' },
          slots: [{
            component: 'Div',
            content: this.$t('message.LOCATIONS'),
          }],
        },
        {
          key: 'zoneInclusive',
          value: this.pbOptZoneInclusive,
          component: 'IntCheckbox',
          options: {
            class: 'block',
            title: this.$t('message.ZONE_INCLUSIVE'),
          },
        },
        {
          key: 'zoneFilter',
          value: this.pbOptZoneSelected,
          component: 'Tree',
          options: {
            ref: 'zonesTree',
            multiple: true,
            items: this.pbOptAllZones,
          },
        },
        {
          key: 'pbOptSep2',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        },
        {
          key: 'pbOptOnlytext5',
          value: '',
          component: 'Div',
          options: { class: 'mt5 legend legend--bottom-margin' },
          slots: [{
            component: 'Div',
            content: this.$t('message.IMPACT'),
          }],
        },
        {
          key: 'impactFilter',
          value: this.pbOptImpactFilter,
          component: 'ImpactSelector',
          options: {
            multiple: true,
            min: true,
          },
        },
        {
          key: 'pbOptSep3',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        },
        {
          key: 'problemsLeadersLegend',
          component: 'div',
          options: {
            class: 'legend mt10 legend--bottom-margin',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.LEADER'),
          }],
        },
        {
          key: 'problemsLeaders',
          component: 'CheckboxList',
          value: this.pbOptSelectedLeaders,
          options: {
            items: this.pbOptLeaderItems,
          },
        },
      ];
    },
    pbOptSortBy() {
      if (this.allPageOptions && this.allPageOptions.sortBy) {
        return this.allPageOptions.sortBy;
      }
      return { value: 3, desc: true };
    },
    /**
     * Available sorting options for problems
     * NOTE: This is overriden in dmp/archives
     * @return {Array}
     */
    pbOptSortItems() {
      return [
        { id: 0, label: this.$t('message.SORT_BY_PROBLEM') },
        { id: 1, label: this.$t('message.SORT_BY_DEADLINE') },
        { id: 2, label: this.$t('message.SORT_BY_IMPACT') },
        { id: 3, label: this.$t('message.SORT_BY_ENTRY_DATE') },
        { id: 4, label: this.$t('message.SORT_BY_SHEET_LAST_MODIFIED') },
      ];
    },
    /** Types used in the list of checkboxes */
    pbOptAllTypes() {
      return this.App.isOnDemoMode
        ? IAHelp.demoProblemTypes(this)
        : [{ id: 0, type: this.$t('message.UNSPECIFIED'), selected: false }].concat(this.pbOptTypes);
    },
    /** Return the ids of types that should be selected */
    pbOptSelectedTypes() {
      if (this.allPageOptions && this.allPageOptions.selectedTypes) {
        return this.allPageOptions.selectedTypes;
      }
      return [];
    },
    pbOptTypesInclusive() {
      if (this.allPageOptions && !isNaN(this.allPageOptions.typesInclusive)) {
        return this.allPageOptions.typesInclusive;
      }
      return false;
    },
    pbOptZoneInclusive() {
      if (this.allPageOptions && !isNaN(this.allPageOptions.zoneInclusive)) {
        return this.allPageOptions.zoneInclusive;
      }
      return 0;
    },
    pbOptZoneSelected() {
      if (this.allPageOptions && this.allPageOptions.zoneFilter) {
        return this.allPageOptions.zoneFilter;
      }
      return [];
    },
    /** Zones used for the ZonesTree */
    pbOptAllZones() {
      return this.pbOptZones;
    },
    pbOptImpactFilter() {
      const allImpactValues = [0, 1, 2, 3];
      if (this.allPageOptions && this.allPageOptions.impactFilter) {
        return convertImpactFilterToArray(this.allPageOptions.impactFilter);
      }
      return allImpactValues;
    },
    /** Array: Available selectable users in the leader filter */
    pbOptLeaderItems() {
      const usersData = this.pbOptUsers || [];
      return [
        { id: 0, label: this.$t('message.UNASSIGNED') },
        ...usersData.map(user => ({ id: Number(user.id), label: this.$t('fullNameFormat', user) })),
      ];
    },
    /** Array: IDs ofselected leaders */
    pbOptSelectedLeaders() {
      if (this.allPageOptions && this.allPageOptions.problemsLeaders) {
        return this.allPageOptions.problemsLeaders;
      }
      return [];
    },
    /** Array: Available options for sorting problems */
    pbOptSortItems() {
      return [
        { id: 0, label: this.$t('message.SORT_BY_PROBLEM') },
        { id: 1, label: this.$t('message.SORT_BY_DEADLINE') },
        { id: 2, label: this.$t('message.SORT_BY_IMPACT') },
        { id: 3, label: this.$t('message.SORT_BY_ENTRY_DATE') },
        { id: 4, label: this.$t('message.SORT_BY_SHEET_LAST_MODIFIED') },
      ];
    },
    /** If defined, contains all the options above */
    allPageOptions() {
      return null;
    },
    ...mapState({
      pbOptTypes: state => state[GET_PB_TYPES.stateKey] || [],
      pbOptTypesLoading: state => state[GET_PB_TYPES.loadingKey],
      pbOptZones: state => state[GET_ZONES.stateKey] || [],
      pbOptTZonesLoading: state => state[GET_ZONES.loadingKey],
      pbOptUsers: state => state[GET_USERS.stateKey] || [],
      pbOptUsersLoading: state => state[GET_USERS.loadingKey],
    }),
  },
  created() {
    if (this.currentTeam && this.currentTeam.id) {
      // Fetch all types and zones
      if (!this.pbOptTypesLoading) {
        this.$store.dispatch(FETCH_PB_TYPES);
      }
      if (!this.pbOptTZonesLoading) {
        this.$store.dispatch(FETCH_ZONES);
      }
      if (!this.pbOptUsersLoading) {
        this.$store.dispatch(FETCH_USERS);
      }
    }
  },
  methods: {
    pbOptAllTypesUpdated() {},
    pbOptTypeUpdated() {},
    /** Create a string formatted as query params with pbOptions params */
    pbOptQueryParams(options) {
      if (!options) {
        return '';
      }
      let query = '';
      let amp = '';
      if (options.sortBy) {
        query += `sortBy=${options.sortBy.value}&sortDesc=${options.sortBy.desc}`;
        amp = '&';
      }
      if (options.impactFilter) {
        const impactFilter = convertImpactFilterToArray(options.impactFilter);
        query += `${amp}impactFilter=${impactFilter.join(',')}`;
        amp = '&';
      }
      if (options.zoneFilter && options.zoneFilter.length) {
        query += `${amp}zoneFilter=${options.zoneFilter.join(',')}&zoneInc=${!!options.zoneInclusive}`;
        amp = '&';
      }
      if (options.selectedTypes && options.selectedTypes.length) {
        query += `${amp}typesFilter=${options.selectedTypes.join(',')}&typesInclusive=${!!options.typesInclusive}`;
        amp = '&';
      }
      if (options.problemsLeaders) {
        query += `${amp}problemsLeaders=${options.problemsLeaders.join(',')}`;
        amp = '&';
      }
      return query;
    },
    pbOptUserLoaded() {
      if (!this.pbOptTypesLoading) {
        this.$store.dispatch(FETCH_PB_TYPES);
      }
      if (!this.pbOptTZonesLoading) {
        this.$store.dispatch(FETCH_ZONES);
      }
    },
  },
};
</script>
